import { ChevronLeft, ChevronRight, ImagePlus } from 'lucide-react';
import { useImageUrl } from '../db/useImageUrl';
import type { Size } from '../db/images';
import './MainPhoto.css';

/**
 * The photo at the top of the panel. It shows whichever of the item's photos
 * is selected, not only the cover — the strip below it works like a store
 * page's, and this is the big view it feeds.
 *
 * The one place in the app that loads an original rather than a thumbnail.
 * Boards render the 480px copy; this is where the pixels are looked at.
 */
export function MainPhoto({
  imageId,
  frame,
  position,
  count,
  onOpen,
  onAdd,
  onStep,
}: {
  imageId: string | undefined;
  /** The cover's intrinsic size. The box keeps its shape as you cycle. */
  frame: Size | undefined;
  /** 1-based, for the label. */
  position: number;
  count: number;
  onOpen: () => void;
  onAdd: () => void;
  onStep: (delta: -1 | 1) => void;
}) {
  const url = useImageUrl(imageId, 'full');

  if (imageId === undefined) {
    return (
      <button type="button" className="main-photo main-photo--empty" onClick={onAdd}>
        <ImagePlus size={24} aria-hidden />
        <span>Add a photo</span>
      </button>
    );
  }

  return (
    <div className="main-photo" style={{ aspectRatio: ratioOf(frame) }}>
      <button
        type="button"
        className="main-photo__open"
        aria-label={`View photo ${position} of ${count} full screen`}
        onClick={onOpen}
      >
        {url !== undefined && <img className="main-photo__image" src={url} alt="" />}
      </button>

      {/* Nested inside the frame but beside the button, not in it: a button
          inside a button is invalid, and expanding the photo when you meant
          to step to the next one is the bug that follows from getting it
          wrong. */}
      {count > 1 && (
        <div className="main-photo__nav">
          <button
            type="button"
            className="main-photo__step"
            aria-label="Previous photo"
            disabled={position <= 1}
            onClick={() => onStep(-1)}
          >
            <ChevronLeft size={18} aria-hidden />
          </button>
          <button
            type="button"
            className="main-photo__step"
            aria-label="Next photo"
            disabled={position >= count}
            onClick={() => onStep(1)}
          >
            <ChevronRight size={18} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The frame holds the cover's proportions and every other photo is contained
 * inside it. Sizing to each photo in turn would shunt the four sections below
 * up and down as you cycle, which is the one thing this panel must not do.
 *
 * Clamped to 1:2 like the board's tiles: a screenshot of a whole product page
 * would otherwise make a frame taller than the panel.
 */
function ratioOf(frame: Size | undefined): string {
  if (frame === undefined || frame.w <= 0 || frame.h <= 0) return '4 / 3';

  const tall = Math.min(frame.h / frame.w, 2);
  return `${frame.w} / ${Math.round(frame.w * tall)}`;
}
